/**
 * `/automations` — SMB trigger assignment management page (P2.5-C).
 *
 * Shows the three trigger types as cards (Lead Qualifier, Booking Bot,
 * Content Drafter). Each card displays the assigned agent (if any), a dropdown
 * to assign a different agent, and a toggle to enable/disable the trigger.
 *
 * Data:
 *   - Trigger assignments: GET /api/v1/triggers
 *   - Agent list: GET /api/v1/agents (for the assignment dropdown)
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import { useAgentsList } from "@/hooks/use-agents";
import { useTriggers, useUpsertTrigger } from "@/hooks/use-agents";
import { ApiError } from "@/lib/api-client";
import type { TriggerType } from "@simpliwork/api-schema";

// ── Static metadata for the three trigger types ───────────────────────────────

interface TriggerMeta {
  type: TriggerType;
  label: string;
  description: string;
  requirements: string[];
}

const TRIGGER_META: TriggerMeta[] = [
  {
    type: "lead_qualify",
    label: "Lead Qualifier",
    description: "Auto-qualify new leads as they arrive",
    requirements: ["CRM"],
  },
  {
    type: "lead_book",
    label: "Booking Bot",
    description: "Auto-send booking links to new leads",
    requirements: ["SMS", "Calendar"],
  },
  {
    type: "content_draft",
    label: "Content Drafter",
    description: "Auto-draft content for new posts",
    requirements: ["Content"],
  },
];

// ── Page component ────────────────────────────────────────────────────────────

export function AutomationsPage() {
  const toast = useToast();
  const triggers = useTriggers();
  const agentsList = useAgentsList();
  const upsertTrigger = useUpsertTrigger();

  // Build a lookup map from triggerType → assigned row
  const assignmentByType = new Map(
    (triggers.data?.items ?? []).map((item) => [item.trigger_type, item]),
  );

  async function handleAssign(triggerType: TriggerType, agentId: string | null) {
    try {
      if (agentId === null) {
        await upsertTrigger.mutateAsync({
          triggerType,
          body: { agent_id: null, enabled: false },
        });
        toast.toast({
          title: "Trigger unassigned",
          description: `${triggerType} no longer has an agent assigned.`,
          variant: "success",
        });
      } else {
        const existing = assignmentByType.get(triggerType);
        await upsertTrigger.mutateAsync({
          triggerType,
          body: { agent_id: agentId, enabled: existing?.enabled ?? true },
        });
        toast.toast({
          title: "Trigger assigned",
          description: `Agent assigned to ${triggerType}.`,
          variant: "success",
        });
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.payload.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      toast.toast({ title: "Failed to update trigger", description: message, variant: "error" });
    }
  }

  async function handleToggleEnabled(triggerType: TriggerType, currentlyEnabled: boolean) {
    const existing = assignmentByType.get(triggerType);
    if (!existing) return;
    try {
      await upsertTrigger.mutateAsync({
        triggerType,
        body: { agent_id: existing.agent_id, enabled: !currentlyEnabled },
      });
      toast.toast({
        title: currentlyEnabled ? "Trigger disabled" : "Trigger enabled",
        description: `${triggerType} has been ${currentlyEnabled ? "disabled" : "enabled"}.`,
        variant: "success",
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.payload.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      toast.toast({ title: "Failed to toggle trigger", description: message, variant: "error" });
    }
  }

  const isLoading = triggers.isLoading || agentsList.isLoading;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="automations-loading">
        <Skeleton className="h-8 w-1/3" />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (triggers.error) {
    return (
      <Card data-testid="automations-error">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">
            Couldn't load automations: {triggers.error.message}
          </p>
        </CardContent>
      </Card>
    );
  }

  const agents = agentsList.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6" data-testid="automations-page">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Automations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Assign AI agents to run automatically when business events occur.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TRIGGER_META.map((meta) => {
          const assignment = assignmentByType.get(meta.type);
          const assignedAgentId = assignment?.agent_id ?? null;
          const isEnabled = assignment?.enabled ?? false;
          const assignedAgent = agents.find((a) => a.id === assignedAgentId);

          return (
            <Card key={meta.type} data-testid={`trigger-card-${meta.type}`}>
              <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
                <div>
                  <CardTitle className="text-base">{meta.label}</CardTitle>
                  <CardDescription className="mt-1 text-sm">{meta.description}</CardDescription>
                </div>
                {assignedAgentId ? (
                  isEnabled ? (
                    <Badge variant="default" className="shrink-0">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="shrink-0">
                      Paused
                    </Badge>
                  )
                ) : (
                  <Badge variant="outline" className="shrink-0">
                    Unassigned
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="flex flex-col gap-3 pt-0">
                {/* Requirements badges */}
                {meta.requirements.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {meta.requirements.map((req) => (
                      <span
                        key={req}
                        className="rounded bg-muted px-1.5 py-0.5 text-[0.7rem] font-medium text-muted-foreground"
                      >
                        Needs: {req}
                      </span>
                    ))}
                  </div>
                )}

                {/* Agent assignment dropdown */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor={`agent-select-${meta.type}`}
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Assigned agent
                  </label>
                  <select
                    id={`agent-select-${meta.type}`}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    value={assignedAgentId ?? ""}
                    disabled={upsertTrigger.isPending}
                    onChange={(e) =>
                      handleAssign(meta.type, e.target.value || null)
                    }
                    data-testid={`trigger-agent-select-${meta.type}`}
                  >
                    <option value="">— None —</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                  {assignedAgent && (
                    <p className="text-[0.72rem] text-muted-foreground">
                      Currently: {assignedAgent.name}
                    </p>
                  )}
                </div>

                {/* Enable/disable toggle (only when assigned) */}
                {assignedAgentId && (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant={isEnabled ? "outline" : "default"}
                      disabled={upsertTrigger.isPending}
                      onClick={() => handleToggleEnabled(meta.type, isEnabled)}
                      data-testid={`trigger-toggle-${meta.type}`}
                    >
                      {isEnabled ? "Pause" : "Enable"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
